import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { hasAuthToken } from '../../auth/services/auth.utils';
import { UserStore } from '@stores/user.store';
import { SubscriptionStore } from '@stores/subscription.store';
import { NativeAppService } from '../../core/services/native-app/native-app.servece';
import { AuthService } from '../../auth/services/auth.service';

/**
 * Guard for the in-webview upgrade paywall page (`app/sales/popup`).
 * Mirrors the `prepare()` step of the UPGRADE_MODAL queue case: exchanges the
 * webview apiKey, then closes the webview natively when the user is not
 * authorized or is not eligible for an upgrade (already has a license/yearly).
 */
export const UpgradePaywallGuard: CanActivateFn = async route => {
  const userStore = inject(UserStore);
  const router = inject(Router);
  const subscriptionStore = inject(SubscriptionStore);
  const nativeApp = inject(NativeAppService);
  const authService = inject(AuthService);

  if (!nativeApp.isInsideNativeApp()) {
    return router.createUrlTree(['/app/packages'], {
      queryParams: { ...route.queryParams, action: 'upgrade' },
    });
  }

  const apiKey = route.queryParamMap.get('ApiKey') || route.queryParamMap.get('apiKey');
  if (apiKey) {
    await authService.exchangeAndStoreApiKey(apiKey);
  }

  if (!hasAuthToken()) {
    nativeApp.closePopup();
    return false;
  }

  const [, canOfferPromotions] = await Promise.all([
    userStore.waitForLoading(),
    subscriptionStore.waitForPromotionEligibility(),
  ]);

  if (!canOfferPromotions || userStore.hasLicense() || subscriptionStore.isYearly()) {
    nativeApp.closePopup();
    return false;
  }

  await subscriptionStore.waitForLoadingUpgrades();
  if (!subscriptionStore.canOfferPromotions()) {
    nativeApp.closePopup();
    return false;
  }
  return true;
};
